// We use 0 to indicate that the concurrency should be automatically determined
// Using a const will make the code more readable
export const AUTO_CONCURRENCY = 0;

export const MAX_CONCURRENCY = 100;
export const MAX_CONCURRENCY_FOR_TEAM_PLAN = 50;
export const MAX_CONCURRENCY_FOR_STARTER_PLAN = 8;

// Staging ECS machines are smaller, so they run fewer workers per container.
export const WORKERS_PER_RUNNER_STAGING = 2;
export const WORKERS_PER_RUNNER_PRODUCTION = 4;
/**
 * Returns the number of parallel Playwright workers each ECS container (runner) can handle.
 * Staging machines are smaller → 2 workers; production → 4 workers.
 */
export function getWorkersPerRunner(): number {
  const env = process.env.ENVIRONMENT ?? process.env.NEXT_PUBLIC_VERCEL_ENV;
  return env === 'production' ? WORKERS_PER_RUNNER_PRODUCTION : WORKERS_PER_RUNNER_STAGING;
}

/**
 * Clamps the single-run worker cap to [1, maxConcurrency].
 * The absolute cap is resolved upstream (from metadata) before reaching this point.
 */
export function deriveSingleRunMaxConcurrency(maxConcurrency: number, singleRunMaxWorkers: number): number {
  return Math.max(1, Math.min(Math.max(0, maxConcurrency), singleRunMaxWorkers));
}

// Fallback max runners cap. Runtime shard allocation derives runner count from:
// ceil(maxConcurrency / workersPerRunner) via deriveMaxRunnersFromConcurrency().
export const MAX_RUNNERS = 100;
export const MAX_RUNNERS_FOR_TEAM_PLAN = 50;
export const MAX_RUNNERS_FOR_STARTER_PLAN = 4;
export const MAX_RUNNERS_FOR_HOBBY_PLAN = 1;

/**
 * Derives the effective runner cap from max concurrency and per-runner worker capacity.
 */
export function deriveMaxRunnersFromConcurrency({
  maxConcurrency,
  workersPerRunner,
}: {
  maxConcurrency: number;
  workersPerRunner: number;
}): number {
  return Math.max(1, Math.ceil(Math.max(0, maxConcurrency) / Math.max(1, workersPerRunner)));
}

/**
 * Cap a resolved worker target before allocation.
 *
 * Order of caps (all must hold):
 *  - eligible tests/specs (when known; 0 means "unknown", which does NOT cap —
 *    the scout path re-caps with real counts from `--list` at scale time)
 *  - single-run maximum workers (workload/plan execution policy)
 *  - org maximum concurrency (plan entitlement)
 * Result is always >= 1.
 */
export function capWorkerBudget({
  requestedWorkers,
  eligibleTests,
  singleRunMaxWorkers,
  orgMaxConcurrency,
}: {
  requestedWorkers: number;
  eligibleTests: number;
  singleRunMaxWorkers: number;
  orgMaxConcurrency: number;
}): number {
  let capped = Math.max(1, Math.floor(requestedWorkers));
  if (eligibleTests > 0) {
    capped = Math.min(capped, Math.floor(eligibleTests));
  }
  capped = Math.min(capped, Math.max(1, Math.floor(singleRunMaxWorkers)), Math.max(1, Math.floor(orgMaxConcurrency)));
  return Math.max(1, capped);
}

/**
 * Split a total worker budget into exact per-shard Playwright worker assignments.
 *
 * Balanced allocation: use as few runners as possible without exceeding
 * maxWorkersPerRunner, then spread workers evenly, giving the remainder to the
 * earliest shards:
 *   shardCount = ceil(total / maxWorkersPerRunner)
 *   base       = floor(total / shardCount)
 *   earliest (total - base * shardCount) shards get base + 1
 *
 * Examples at maxWorkersPerRunner=4: 1 -> [1], 3 -> [3], 6 -> [3,3],
 * 8 -> [4,4], 10 -> [4,3,3].
 *
 * The returned array is the single source of truth for both shard count
 * (array length) and each shard's exact --workers value; every launch path
 * (direct spawn and DB queue) must use these numbers verbatim so queue
 * accounting always equals launched Playwright workers.
 */
export function allocateBalancedWorkers({
  totalWorkers,
  maxWorkersPerRunner,
}: {
  totalWorkers: number;
  maxWorkersPerRunner: number;
}): number[] {
  const total = Math.max(1, Math.floor(totalWorkers));
  const maxPerRunner = Math.max(1, Math.floor(maxWorkersPerRunner));
  const shardCount = Math.ceil(total / maxPerRunner);
  const base = Math.floor(total / shardCount);
  const remainder = total - base * shardCount;
  return Array.from({ length: shardCount }, (_, i) => (i < remainder ? base + 1 : base));
}

export type RunAllocation = {
  shardTotal: number;
  /**
   * Exact per-shard Playwright worker assignments (index 0 = shard 1). Present
   * only for budgeted runs; every launch path must use these numbers verbatim
   * (TestRunnerRequest.workers, STABLY_ASSIGNED_WORKERS, scale response).
   */
  workersByShard?: number[];
};

/**
 * The single allocation decision for a run — budgeted or legacy.
 *
 * Both allocation sites (startRunner's prep-time path and scale-run's
 * scout-time path) call THIS function, so the direct-spawn and DB-queued
 * paths cannot drift: same inputs -> same shard count and same exact
 * per-shard assignments, by construction.
 *
 * - resolvedWorkerTarget set: cap by eligible tests (0 = unknown, no cap),
 *   single-run max, and org max, then bound the shard (container) count by
 *   maxShards when the caller can prove one, then split with
 *   allocateBalancedWorkers.
 * - resolvedWorkerTarget undefined: legacy shard-count-only math; workers per
 *   shard remain governed by the physical per-runner capacity cap. No maxShards
 *   bound is needed there — legacy eligibleTests is already the spec count.
 */
export function decideRunAllocation({
  resolvedWorkerTarget,
  eligibleTests,
  expectedWorkers,
  singleRunMaxConcurrency,
  orgMaxConcurrency,
  workersPerRunner,
  maxShards,
}: {
  /**
   * Output of `resolveCloudTarget` — the intent collapsed against the live plan
   * ceiling. Auto resolves one of these just as Fixed does, so this is NOT a
   * "Fixed budget"; undefined means legacy.
   */
  resolvedWorkerTarget: number | undefined;
  eligibleTests: number;
  expectedWorkers: number;
  singleRunMaxConcurrency: number;
  orgMaxConcurrency: number;
  workersPerRunner: number;
  /**
   * Optional upper bound on the number of shards (= containers) for budgeted
   * runs, used when the caller knows Playwright cannot fill more than this many
   * shards (see getScaleMaxShards). Applied by LOWERING the total budget to
   * maxShards * workersPerRunner rather than packing more workers into fewer
   * containers — per-shard workers must never exceed physical per-runner
   * capacity. undefined = no bound.
   */
  maxShards?: number;
}): RunAllocation {
  if (resolvedWorkerTarget != null) {
    const totalWorkers = capWorkerBudget({
      requestedWorkers: resolvedWorkerTarget,
      eligibleTests,
      singleRunMaxWorkers: singleRunMaxConcurrency,
      orgMaxConcurrency,
    });
    const boundedWorkers =
      maxShards != null && maxShards >= 1 ?
        Math.min(totalWorkers, Math.floor(maxShards) * Math.max(1, Math.floor(workersPerRunner)))
      : totalWorkers;
    const workersByShard = allocateBalancedWorkers({
      totalWorkers: boundedWorkers,
      maxWorkersPerRunner: workersPerRunner,
    });
    return { shardTotal: workersByShard.length, workersByShard };
  }

  return {
    shardTotal: calculateShardCount({
      maxConcurrency: singleRunMaxConcurrency,
      expectedWorkers,
      workersPerRunner,
    }),
  };
}

/**
 * Calculate how many runners (shards) to use for a test run.
 *
 * Rules:
 * - Never use more workers than maxConcurrency
 * - Distribute workers across runners based on workersPerRunner
 *
 * Examples (production, workersPerRunner=4):
 *   maxConcurrency=8, tests=16 → 8 workers across 2 runners (4+4)
 *
 * Examples (staging, workersPerRunner=2):
 *   maxConcurrency=8, tests=16 → 8 workers across 4 runners (2+2+2+2)
 */
export function calculateShardCount({
  maxConcurrency,
  expectedWorkers,
  workersPerRunner,
}: {
  maxConcurrency: number;
  expectedWorkers: number;
  workersPerRunner: number;
}): number {
  const targetWorkers = Math.min(maxConcurrency, expectedWorkers) || 1;
  const maxRunners = deriveMaxRunnersFromConcurrency({ maxConcurrency, workersPerRunner });
  return Math.min(Math.ceil(targetWorkers / workersPerRunner), maxRunners) || 1;
}
